import { stringifyUnknown } from "./stringifyUnknown";

/**
 * Serializable summary of an unknown thrown/caught value.
 *
 * Discriminated on which key is present: `name`/`message` for an `Error`, `value` for anything else.
 */
export type ErrorCauseSummary = { name: string; message: string } | { value: string };

/**
 * Summarize an unknown thrown/caught value into something a log transport can serialize.
 *
 * An `Error` attached to a log payload serializes to `{}` (its `name`/`message` are non-enumerable),
 * so an error `cause` has to be flattened before it is logged. This does that flattening, and falls
 * back to a placeholder instead of throwing for values `String()` cannot handle.
 *
 * Prefer this over the inline `x instanceof Error ? { name: x.name, message: x.message } : { value: String(x) }`
 * idiom. When you need a real `Error` rather than a loggable summary, use `coerceError`.
 */
export function summarizeErrorCause(cause: unknown): ErrorCauseSummary {
  if (cause instanceof Error) {
    return { name: cause.name, message: cause.message };
  }
  return { value: stringifyUnknown(cause) };
}
